// Archivos de un ticket: evidencia multimedia (fotos/video/voz) Y documentos (PDF/Word/Excel…),
// TODO en un mismo campo. Dos usos:
//  - PendingEvidenciaField → al CREAR (colecciona archivos; se suben al guardar el ticket).
//  - EvidenciaSection      → en el DETALLE (sube al instante; borrar solo Super Admin).
// UI: dropzone (clic, arrastrar o pegar) + miniaturas uniformes (foto, video con ▶, audio con play,
// documento con ícono). Las fotos y videos se ven en un VISOR (lightbox) en la misma pantalla.
import { MODAL_TABLA } from "@/components/ui/panel-widths";
import { useEffect, useId, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Upload, Loader2, Download } from "lucide-react";
import { BotonPegarArchivos, usePegadoArchivos } from "@/components/ui/pegado-archivos";
import { toast } from "sonner";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";
import { VoiceRecorderButton } from "./VoiceRecorder";
import {
  MAX_ADJUNTOS,
  ACCEPT_ALL,
  toPendingAdjunto,
  fetchTicketAdjuntos,
  saveTicketAdjuntos,
  deleteTicketAdjunto,
  setTicketAdjuntoVisibleCliente,
  type AdjuntoTipo,
  type PendingAdjunto,
  type TicketAdjunto,
} from "@/lib/portal-tickets/tickets-adjuntos";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { DeleteConfirmationDialog } from "@/components/admin/DeleteConfirmationDialog";

// Visor, miniatura y tipo visual viven en components/ui (los reusan cobranza y autorizaciones).
// Se re-exportan aquí para no romper a quien ya los importa por esta ruta.
import { MediaLightbox, type VisorItem } from "@/components/ui/media-lightbox";
import { EvidenciaTile } from "@/components/ui/evidence-tile";
import { esVisible } from "@/components/ui/media-tipo";
import { InfoTooltip } from "@/components/ui/info-tooltip";
export { MediaLightbox, type VisorItem } from "@/components/ui/media-lightbox";
export { EvidenciaTile } from "@/components/ui/evidence-tile";
export { esVisible } from "@/components/ui/media-tipo";

const HINT = `Fotos, videos, audio y documentos (PDF, Word, Excel, PowerPoint, TXT, CSV, ZIP). Fotos hasta 10 MB, videos hasta 50 MB y lo demás hasta 25 MB.`;

// Los .md se abren en un modal renderizado (no en pestaña); el resto de documentos abren aparte.
function esMarkdown(nombre?: string) {
  const ext = (nombre?.split(".").pop() || "").toLowerCase();
  return ext === "md" || ext === "markdown";
}

// Etiqueta legible del tipo de adjunto (para el diálogo de confirmación de borrado).
function etiquetaTipo(tipo: AdjuntoTipo) {
  return tipo === "foto" ? "imagen" : tipo === "video" ? "video" : tipo === "audio" ? "audio" : "documento";
}

// ── Modal que renderiza un .md formateado (títulos, tablas, negritas) ──
// Descarga el texto y lo pinta con react-markdown + GFM. `fetch(...).text()` decodifica
// SIEMPRE como UTF-8, así que no hay "símbolos raros" ni para archivos viejos sin charset.
// react-markdown no ejecuta HTML crudo por defecto → seguro para archivos de terceros.
function MarkdownModal({ url, nombre, onClose }: { url: string; nombre?: string; onClose: () => void }) {
  const [text, setText] = useState<string | null>(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    let cancel = false;
    setText(null);
    setError(false);
    fetch(url)
      .then((r) => {
        if (!r.ok) throw new Error(String(r.status));
        return r.text();
      })
      .then((t) => !cancel && setText(t))
      .catch(() => !cancel && setError(true));
    return () => {
      cancel = true;
    };
  }, [url]);

  // Descarga: para URLs http de Supabase se agrega `?download=<nombre>` (fuerza
  // Content-Disposition: attachment); para blobs (preview local al crear) basta el
  // atributo `download`.
  const nombreDescarga = nombre || "documento.md";
  const downloadHref = url.startsWith("blob:")
    ? url
    : `${url}${url.includes("?") ? "&" : "?"}download=${encodeURIComponent(nombreDescarga)}`;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className={MODAL_TABLA}>
        <DialogHeader>
          <div className="flex items-center justify-between gap-3 pr-10">
            <DialogTitle className="truncate text-left">{nombre || "Documento"}</DialogTitle>
            <InfoTooltip content="Descargar"><a
              href={downloadHref}
              download={nombreDescarga}
              className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md border border-border px-2.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              aria-label={`Descargar ${nombreDescarga}`}
            >
              <Download className="size-4" />
              Descargar
            </a></InfoTooltip>
          </div>
        </DialogHeader>
        <div className="max-h-[70vh] overflow-y-auto pr-1">
          {error ? (
            <p className="text-sm text-destructive">
              No se pudo cargar el archivo. Puedes bajarlo con Descargar.
            </p>
          ) : text === null ? (
            <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" /> Cargando…
            </div>
          ) : (
            <article className="prose prose-sm max-w-none dark:prose-invert prose-pre:overflow-x-auto prose-table:block prose-table:overflow-x-auto">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
            </article>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Zona de subida: clic, arrastrar, pegar (Ctrl/Cmd+V sobre la zona o en todo el modal que la
// contiene) o botón "Pegar del portapapeles" (Clipboard API: es la única vía en el teléfono).
// Muchos usuarios no guardan el archivo: lo copian de la web, de WhatsApp o de una captura y lo
// pegan. También graba voz. El pegado y el arrastre son el estándar de components/ui/pegado-archivos.
// Con `arrastrandoExterno` el campo que la contiene ya escucha pegado y arrastre (PendingEvidenciaField).
function Dropzone({
  onFiles,
  disabled,
  restantes,
  enModal = false,
  arrastrandoExterno,
}: {
  onFiles: (files: File[]) => void;
  disabled?: boolean;
  /** Lugares que quedan; se muestra junto al hint. */
  restantes: number;
  /** Escuchar pegado y arrastre en todo el modal (si nadie más lo hace). */
  enModal?: boolean;
  /** Estado de arrastre cuando quien escucha es el campo que contiene la zona. */
  arrastrandoExterno?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const zonaRef = useRef<HTMLDivElement>(null);
  const externo = arrastrandoExterno !== undefined;
  const { arrastrando, zonaProps } = usePegadoArchivos(zonaRef, (f) => onFiles(f), {
    disabled: disabled || externo,
    enModal,
  });
  const drag = externo ? arrastrandoExterno : arrastrando;

  return (
    <div
      ref={zonaRef}
      role="button"
      tabIndex={0}
      aria-label="Subir o pegar archivos"
      onClick={() => !disabled && inputRef.current?.click()}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          if (!disabled) inputRef.current?.click();
        }
      }}
      {...(externo ? {} : zonaProps)}
      className={cn(
        "flex cursor-pointer flex-col items-center gap-3 rounded-xl border-2 border-dashed px-4 py-5 text-center outline-none transition-colors",
        "focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/15",
        drag ? "border-primary bg-primary/5" : "border-border bg-muted/20 hover:border-primary/50 hover:bg-muted/40",
        disabled && "cursor-not-allowed opacity-60",
      )}
    >
      <span className="grid size-10 place-items-center rounded-full bg-primary/10 text-primary">
        <Upload className="size-[18px]" />
      </span>
      <div className="space-y-0.5">
        <p className="text-[13px] font-medium text-foreground">
          {drag ? "Suelta los archivos aquí" : "Arrastra archivos aquí o haz clic para elegirlos"}
        </p>
        <p className="text-[12px] text-muted-foreground">
          También puedes pegar una captura con Ctrl/Cmd+V.
        </p>
      </div>

      {/* Acciones extra dentro de la zona: no deben abrir el selector de archivos. */}
      <div
        className="flex flex-wrap items-center justify-center gap-2"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <BotonPegarArchivos onFiles={onFiles} disabled={disabled} />
        <VoiceRecorderButton onRecorded={(f) => onFiles([f])} disabled={disabled} />
      </div>

      <p className="max-w-sm text-[11px] leading-relaxed text-muted-foreground">
        {HINT} {restantes === 1 ? "Queda 1 lugar." : `Quedan ${Math.max(0, restantes)} lugares.`}
      </p>

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT_ALL}
        multiple
        hidden
        onChange={(e) => {
          onFiles(Array.from(e.target.files ?? []));
          if (inputRef.current) inputRef.current.value = "";
        }}
      />
    </div>
  );
}


// ── CREAR: campo controlado de archivos pendientes (se suben al guardar el ticket) ──
export function PendingEvidenciaField({
  value,
  onChange,
  disabled,
  label = "Evidencia (opcional)",
}: {
  value: PendingAdjunto[];
  onChange: (next: PendingAdjunto[]) => void;
  disabled?: boolean;
  label?: string;
}) {
  const lleno = value.length >= MAX_ADJUNTOS;
  const [viewer, setViewer] = useState<number | null>(null);
  const [mdDoc, setMdDoc] = useState<{ url: string; nombre?: string } | null>(null);
  const visor: VisorItem[] = value
    .filter((p) => esVisible(p.tipo))
    .map((p) => ({ src: p.previewUrl, tipo: p.tipo, nombre: p.nombre }));

  // Último valor conocido, también entre dos pegados seguidos que llegan antes del re-render
  // (Win+V elige capturas una tras otra): cada pegado se suma a lo anterior, no lo reemplaza.
  const rootRef = useRef<HTMLDivElement>(null);
  const valueRef = useRef(value);
  valueRef.current = value;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const addArchivos = (arr: File[], pegado = false) => {
    if (!arr.length) return;
    const actual = valueRef.current;
    const space = MAX_ADJUNTOS - actual.length;
    if (space <= 0) {
      toast.error(`Máximo ${MAX_ADJUNTOS} archivos.`);
      return;
    }
    if (arr.length > space) toast.error(`Solo se agregaron ${space}; el máximo es ${MAX_ADJUNTOS}.`);
    const nuevos = arr
      .slice(0, space)
      .map((f) => toPendingAdjunto(f))
      .filter((p): p is PendingAdjunto => !!p);
    if (!nuevos.length) return;
    const next = [...actual, ...nuevos];
    valueRef.current = next;
    onChangeRef.current(next);
    if (pegado) {
      toast.success(nuevos.length === 1 ? "Archivo pegado como evidencia" : `${nuevos.length} archivos pegados como evidencia`);
    }
  };
  const addArchivosRef = useRef(addArchivos);
  addArchivosRef.current = addArchivos;

  // Pegado (Ctrl/Cmd+V, Win+V) y arrastre en CUALQUIER parte del modal que contiene el campo, no
  // solo sobre la zona: al abrir el modal el foco suele estar en un textarea. Escucha el campo
  // entero (no la zona) para avisar del máximo aunque la zona ya no se muestre. Quien ya manejó el
  // pegado (p. ej. la descripción de "Crear ticket") llama preventDefault y aquí se ignora.
  const { arrastrando, zonaProps } = usePegadoArchivos(
    rootRef,
    (files, origen) => addArchivosRef.current(files, origen === "pegar"),
    { disabled },
  );

  const remove = (id: string) => {
    const target = value.find((p) => p.id === id);
    if (target) URL.revokeObjectURL(target.previewUrl);
    onChange(value.filter((p) => p.id !== id));
  };

  return (
    <div ref={rootRef} className="space-y-2" {...zonaProps}>
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium text-muted-foreground">{label}</Label>
        <span className="text-xs tabular-nums text-muted-foreground">
          {value.length} de {MAX_ADJUNTOS} archivos
        </span>
      </div>

      {value.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {value.map((p) => (
            <EvidenciaTile
              key={p.id}
              src={p.previewUrl}
              tipo={p.tipo}
              nombre={p.nombre}
              onRemove={() => remove(p.id)}
              onOpen={esVisible(p.tipo) ? () => setViewer(visor.findIndex((v) => v.src === p.previewUrl)) : undefined}
              onOpenDoc={esMarkdown(p.nombre) ? () => setMdDoc({ url: p.previewUrl, nombre: p.nombre }) : undefined}
            />
          ))}
        </div>
      )}

      {!disabled && !lleno && (
        <Dropzone onFiles={(f) => addArchivos(f)} restantes={MAX_ADJUNTOS - value.length} arrastrandoExterno={arrastrando} />
      )}

      {viewer !== null && visor[viewer] && (
        <MediaLightbox items={visor} index={viewer} onClose={() => setViewer(null)} onIndex={setViewer} />
      )}

      {mdDoc && <MarkdownModal url={mdDoc.url} nombre={mdDoc.nombre} onClose={() => setMdDoc(null)} />}
    </div>
  );
}

// ── DETALLE: sección en vivo (sube al instante; borrar solo Super Admin) ──
export function EvidenciaSection({
  ticketId,
  canDelete,
  readOnly,
}: {
  ticketId: string;
  canDelete: boolean;
  readOnly?: boolean;
}) {
  const { user } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [visibleId, setVisibleId] = useState<number | null>(null);
  const [viewer, setViewer] = useState<number | null>(null);
  const switchId = useId();
  // Arranca apagado a propósito: publicar de más es irreversible (el cliente ya lo vio) y
  // solo un Super Admin puede volver a ocultarlo; no publicar se corrige con este mismo switch.
  const [visibleCliente, setVisibleCliente] = useState(false);

  const {
    data: adjuntos = [],
    refetch,
    isLoading,
  } = useQuery({
    queryKey: ["ticket-adjuntos", ticketId],
    queryFn: () => fetchTicketAdjuntos(ticketId),
  });

  const lleno = adjuntos.length >= MAX_ADJUNTOS;
  // La misma política de RLS gobierna el borrado y el cambio de visibilidad (UPDATE de
  // Super Admin): si no puede borrar, el UPDATE no toca ninguna fila y el switch mentiría.
  const puedeCambiarVisibilidad = !readOnly && canDelete;
  const [mdDoc, setMdDoc] = useState<{ url: string; nombre?: string } | null>(null);
  const [adjBorrar, setAdjBorrar] = useState<TicketAdjunto | null>(null);
  const visor: VisorItem[] = adjuntos
    .filter((a) => esVisible(a.tipo))
    .map((a) => ({ src: a.url, tipo: a.tipo, nombre: a.nombre }));

  const addArchivos = async (arr: File[]) => {
    if (!arr.length) return;
    const space = MAX_ADJUNTOS - adjuntos.length;
    if (space <= 0) {
      toast.error(`Máximo ${MAX_ADJUNTOS} archivos.`);
      return;
    }
    const chosen = arr
      .slice(0, space)
      .map((f) => toPendingAdjunto(f))
      .filter((p): p is PendingAdjunto => !!p);
    if (!chosen.length) return;
    const antes = adjuntos.length;
    setUploading(true);
    await saveTicketAdjuntos(ticketId, user?.id, chosen, { visibleCliente });
    chosen.forEach((p) => URL.revokeObjectURL(p.previewUrl));
    setUploading(false);
    // saveTicketAdjuntos es best-effort y avisa cada fallo: solo se confirma si algo entró.
    const frescos = await refetch();
    if ((frescos.data?.length ?? 0) > antes) {
      toast.success(
        visibleCliente ? "Archivos subidos; el cliente los ve en su app." : "Archivos subidos; solo los ve el equipo.",
      );
    }
  };

  const cambiarVisibilidad = async (adj: TicketAdjunto) => {
    const siguiente = !adj.visibleCliente;
    setVisibleId(adj.id);
    const ok = await setTicketAdjuntoVisibleCliente(adj, siguiente);
    setVisibleId(null);
    if (ok) {
      toast.success(siguiente ? "Ahora el cliente lo ve en su app." : "Ya no lo ve el cliente.");
      await refetch();
    }
  };

  const remove = async (adj: TicketAdjunto) => {
    setDeletingId(adj.id);
    const ok = await deleteTicketAdjunto(adj);
    setDeletingId(null);
    if (ok) {
      toast.success("Archivo eliminado");
      await refetch();
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-semibold text-foreground">Evidencia y documentos</Label>
        <span className="text-xs text-muted-foreground">
          {adjuntos.length}/{MAX_ADJUNTOS}
        </span>
      </div>

      {isLoading ? (
        <p className="text-xs text-muted-foreground">Cargando…</p>
      ) : (
        <>
          {adjuntos.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {adjuntos.map((a) => (
                <EvidenciaTile
                  key={a.id}
                  src={a.url}
                  tipo={a.tipo}
                  nombre={a.nombre}
                  onRemove={canDelete ? () => setAdjBorrar(a) : undefined}
                  onOpen={esVisible(a.tipo) ? () => setViewer(visor.findIndex((v) => v.src === a.url)) : undefined}
                  onOpenDoc={esMarkdown(a.nombre) ? () => setMdDoc({ url: a.url, nombre: a.nombre }) : undefined}
                  busy={deletingId === a.id}
                  visibleCliente={a.visibleCliente === true}
                  onToggleVisible={puedeCambiarVisibilidad ? () => cambiarVisibilidad(a) : undefined}
                  cambiandoVisible={visibleId === a.id}
                />
              ))}
            </div>
          )}

          {!readOnly && !lleno && (
            <div className="space-y-2">
              <div
                className={cn(
                  "flex items-start gap-3 rounded-lg border px-3 py-2 transition-colors",
                  visibleCliente ? "border-primary/40 bg-primary/[0.05]" : "border-border bg-muted/30",
                )}
              >
                <Switch
                  id={switchId}
                  checked={visibleCliente}
                  onCheckedChange={setVisibleCliente}
                  disabled={uploading}
                  className="mt-0.5"
                />
                <Label htmlFor={switchId} className="cursor-pointer text-[12.5px] font-medium leading-tight text-foreground">
                  Visible para el cliente
                  <span className="mt-0.5 block text-[11px] font-normal text-muted-foreground">
                    {visibleCliente
                      ? "Lo que subas ahora aparece en la app del cliente."
                      : "Lo que subas ahora queda solo para el equipo."}
                  </span>
                </Label>
              </div>

              {uploading ? (
                <div className="flex items-center justify-center gap-2 rounded-lg border border-dashed border-border p-4 text-xs text-muted-foreground">
                  <Loader2 className="size-4 animate-spin" /> Subiendo…
                </div>
              ) : (
                <Dropzone onFiles={addArchivos} restantes={MAX_ADJUNTOS - adjuntos.length} />
              )}
            </div>
          )}

          {!readOnly && lleno && (
            <p className="rounded-md bg-muted/50 px-3 py-2 text-[12px] text-muted-foreground">
              El ticket ya tiene {MAX_ADJUNTOS} archivos, el máximo.
              {canDelete ? " Elimina alguno para subir otro." : " Un Super Admin puede eliminar alguno."}
            </p>
          )}

          {!adjuntos.length && readOnly && <p className="text-xs text-muted-foreground">Sin archivos.</p>}
        </>
      )}


      {viewer !== null && visor[viewer] && (
        <MediaLightbox items={visor} index={viewer} onClose={() => setViewer(null)} onIndex={setViewer} />
      )}

      {mdDoc && <MarkdownModal url={mdDoc.url} nombre={mdDoc.nombre} onClose={() => setMdDoc(null)} />}

      <DeleteConfirmationDialog
        open={!!adjBorrar}
        onOpenChange={(o) => !o && setAdjBorrar(null)}
        title={adjBorrar ? `¿Eliminar ${etiquetaTipo(adjBorrar.tipo)}?` : "¿Eliminar archivo?"}
        description={adjBorrar ? `Se eliminará "${adjBorrar.nombre}".` : ""}
        warningMessage="Esta acción no se puede deshacer."
        isLoading={!!adjBorrar && deletingId === adjBorrar.id}
        onConfirm={async () => {
          if (!adjBorrar) return;
          await remove(adjBorrar);
          setAdjBorrar(null);
        }}
      />
    </div>
  );
}
